import * as React from "react";

import {
  useTable,
  type Table,
  type Updater,
  type UseTableOptions,
} from "@/shared/foundation/primitives/Table";

import { ActiveFilters, Filters } from "./Filters";
import type { Filter, FilterMatchMode } from "./Filters/FilterSchema";
import { ResourceViewActions } from "./ResourceViewActions";
import { ResourceViewRoot } from "./ResourceViewRoot";
import { Selection } from "./Selection";

/**
 * Options for `useResourceView`. Extends `UseTableOptions` with the two
 * filter-state seeds.
 */
export interface UseResourceViewOptions<TData> extends UseTableOptions<TData> {
  /**
   * Initial applied filters seeded into the hook at mount. Captured once;
   * later changes to this prop are ignored — the hook owns filter state from
   * then on. Use this for restoring saved or URL-encoded filter sets at
   * initial render.
   */
  initialFilters?: Filter[];
  /**
   * Initial match mode seeded into the hook at mount. Captured once;
   * defaults to `"AND"`.
   */
  initialMatchMode?: FilterMatchMode;
}

/**
 * Returned by `useResourceView`. The `table` property is the nested
 * `Table<TData>` instance from `useTable` — pass it to `<DataTable>` and
 * read table-side fields off it (sort, selection, pageSize, etc.). The
 * filter-side fields live directly on the view.
 *
 * **Always destructure into specific fields.** Do not pass `view` itself as
 * a prop (`<Foo view={view} />`). The view bundles both layers; threading it
 * whole forces the receiving component to re-render on changes from either
 * layer, defeating the separation. The hook intentionally does NOT memoize
 * its return value for this reason — each piece inside (`table`, mutators,
 * state) has its own stable identity, but the wrapper does not.
 */
export interface ResourceView<TData> {
  /** The underlying table instance. Pass `view.table` to `<DataTable>` and
   *  read it for table-side fields (`sort`, `selection`, `pageSize`, etc.).
   *  Identity is stable across filter-only changes — components depending
   *  on table state don't re-render when only filter state moves. */
  readonly table: Table<TData>;

  // ── Filter state ──
  readonly filters: Filter[];
  readonly matchMode: FilterMatchMode;

  // ── Mutators ──
  readonly setMatchMode: (updater: Updater<FilterMatchMode>) => void;
  /** Append a single filter. Resets `currentPage` to 0. */
  readonly addFilter: (filter: Filter) => void;
  /** Remove the filter at `index`. Resets `currentPage` to 0. */
  readonly removeFilter: (index: number) => void;
  /** Drop all filters and restore `matchMode` to the value captured from
   *  `initialMatchMode` at mount (default `"AND"`). Resets `currentPage`. */
  readonly clearFilters: () => void;
}

/**
 * Hook for a paginated, sortable, filterable resource view.
 */
export function useResourceView<TData>(
  opts: UseResourceViewOptions<TData>,
): ResourceView<TData> {
  const table = useTable<TData>(opts);

  // ── Mount snapshots ─────────────────────────────────────────────────
  const [initialFilters] = React.useState<Filter[]>(
    () => opts.initialFilters ?? [],
  );
  const [initialMatchMode] = React.useState<FilterMatchMode>(
    () => opts.initialMatchMode ?? "AND",
  );

  // ── Live state ──────────────────────────────────────────────────────
  const [filters, setFilters] = React.useState<Filter[]>(initialFilters);
  const [matchMode, setMatchModeInternal] =
    React.useState<FilterMatchMode>(initialMatchMode);

  // ── Mutators ────────────────────────────────────────────────────────
  // Each filter mutator calls `table.resetCurrentPage()` to wipe page +
  // selection — same behavior the legacy `useDataTableState` had.
  const { resetCurrentPage } = table;

  const addFilter = React.useCallback(
    (filter: Filter) => {
      setFilters((prev) => [...prev, filter]);
      resetCurrentPage();
    },
    [resetCurrentPage],
  );

  const removeFilter = React.useCallback(
    (index: number) => {
      setFilters((prev) => prev.filter((_, i) => i !== index));
      resetCurrentPage();
    },
    [resetCurrentPage],
  );

  const clearFilters = React.useCallback(() => {
    setFilters([]);
    setMatchModeInternal(initialMatchMode);
    resetCurrentPage();
  }, [initialMatchMode, resetCurrentPage]);

  const setMatchMode = React.useCallback(
    (updater: Updater<FilterMatchMode>) => {
      setMatchModeInternal(updater);
      resetCurrentPage();
    },
    [resetCurrentPage],
  );

  // ── Instance ────────────────────────────────────────────────────────
  // Return a plain object literal — no useMemo wrapper. The view is meant
  // to be destructured by consumers; its own identity is irrelevant
  // because nothing should read it wholesale.
  //
  // ANTI-PATTERN: `<SomeComponent view={view} />`.
  return {
    table,
    filters,
    matchMode,
    addFilter,
    removeFilter,
    clearFilters,
    setMatchMode,
  };
}

/**
 * Compound namespace bundling the data-coupled UI affordances and layout
 * shell. Consumers compose them around `<DataTable>` (a foundation
 * primitive, imported directly):
 *
 * @example
 * ```tsx
 * <ResourceView.Root>
 *   <ResourceView.Actions>
 *     <ResourceView.Filters
 *       filterDefinitions={FIELDS}
 *       onFilterSelected={view.addFilter}
 *     />
 *     <ColumnVisibility ... />
 *     <ResourceView.Selection ... />
 *   </ResourceView.Actions>
 *   <ResourceView.ActiveFilters ... />
 *   <DataTable table={view.table} ... />
 * </ResourceView.Root>
 * ```
 */
export const ResourceView = {
  Root: ResourceViewRoot,
  Actions: ResourceViewActions,
  Filters,
  ActiveFilters,
  Selection,
};
